"use client";
import { I18N_NAMESPACE } from "@/config/i18n";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";

export const zeroPad = (num: number): string => String(num).padStart(2, "0");

interface CountdownProps {
  endTime: string;
  labelClassName?: string;
  valueClassName?: string;
  className?: string;
  shortLabel?: boolean;
  onTimerEnd?: () => void;
}

const CountDown: React.FC<CountdownProps> = ({
  endTime,
  labelClassName = "",
  valueClassName = "",
  className = "",
  shortLabel = false,
  onTimerEnd = () => {},
}) => {
  const t = useTranslations(I18N_NAMESPACE.GENERAL);
  const calculateTimeLeft = () => {
    const unixTimestampWithOffset = new Date(endTime).getTime() / 1000;

    const currentTimeInMillis = new Date().getTime();
    const currentUnixTimestamp = Math.floor(currentTimeInMillis / 1000);
    const difference = unixTimestampWithOffset - currentUnixTimestamp;

    let timeLeft: { [key: string]: number } = {};

    if (difference > 0) {
      const milliseconds = difference * 1000;
      const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (milliseconds % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
      if (days === 0 && hours === 0 && minutes === 0 && seconds === 1) {
        onTimerEnd && onTimerEnd();
      }

      timeLeft = {
        [t("countdown-days")]: days,
        [t("countdown-hours")]: hours,
        [t("countdown-minutes")]: minutes,
        [t("countdown-seconds")]: seconds,
      };
    }
    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    const interval = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => {
      clearTimeout(interval);
    };
  });

  const timerComponents: JSX.Element[] = [];

  Object.keys(timeLeft).forEach((interval) => {
    if (interval !== t("countdown-seconds") && timeLeft[interval] === 0) {
      return;
    }
    timerComponents.push(
      <div key={interval} className={className} suppressHydrationWarning>
        <span className={valueClassName}>{zeroPad(timeLeft[interval])}</span>
        <span className={labelClassName}>
          {shortLabel ? interval?.substring(0, 1) : interval}
        </span>
      </div>
    );
  });
  return <>{timerComponents}</>;
};

export default CountDown;
